import api from "src/services/api";

export interface ChatConversation {
  id: number;
  tipo: "PRIVADA" | "GRUPO";
  nombre?: string | null;
  codigo?: string | null;
  nombre_mostrar?: string;
  usuario_privado?: number | null;
  legajo_mostrar?: number | string | null;
  ultimo_mensaje_id?: number | null;
  ultimo_mensaje?: string | null;
  ultimo_mensaje_en?: string | null;
  no_leidos?: number;
}

export interface ChatMessage {
  id: number;
  conversacion_id: number;
  remitente_cedula: number;
  remitente_nombre?: string;
  cuerpo: string;
  creado_en: string;
  hora_envio?: string;
  es_mio: boolean;
  leido_por_otros: number;
  total_otros: number;
  leido_por_todos?: boolean;
}

export interface ChatUser {
  usuario: number;
  cedula: number;
  legajo?: number | string | null;
  nombre: string;
  nombre_corto?: string;
}

export interface ChatPerfil {
  cedula: number;
  nombre_base: string;
  opciones_nombres: string[];
  opciones_apellidos: string[];
  nombre_seleccionado?: string | null;
  apellido_seleccionado?: string | null;
  nombre_visual: string;
}

export interface ChatMember extends ChatUser {
  rol?: "ADMIN" | "MIEMBRO" | string;
  ultimo_leido_mensaje_id?: number | null;
}

export interface ChatPermiso {
  habilitado: boolean;
  puede_gestionar: boolean;
}

export interface ChatWhitelistUser extends ChatUser {
  activo_whitelist: boolean;
}

export const obtenerPermisoChat = async () => {
  const { data } = await api.get<ChatPermiso>("chat/permiso");
  return {
    habilitado: Boolean(data?.habilitado),
    puede_gestionar: Boolean(data?.puede_gestionar),
  };
};

export const obtenerPerfilChat = async () => {
  const { data } = await api.get<ChatPerfil>("chat/perfil");
  return data;
};

export const guardarPerfilChat = async (nombreSeleccionado: string, apellidoSeleccionado: string) => {
  const { data } = await api.post<{ info: string; nombre_seleccionado?: string; apellido_seleccionado?: string }>("chat/perfil", {
    nombre_seleccionado: nombreSeleccionado,
    apellido_seleccionado: apellidoSeleccionado,
  });
  return data;
};

export const listarConversaciones = async () => {
  const { data } = await api.get<{ conversaciones: ChatConversation[] }>("chat/conversaciones");
  return data?.conversaciones || [];
};

export const listarUsuariosChat = async (q?: string) => {
  const { data } = await api.get<{ usuarios: ChatUser[] }>("chat/usuarios", {
    params: q ? { q } : undefined,
  });
  return data?.usuarios || [];
};

export const listarWhitelistChat = async (q?: string) => {
  const { data } = await api.get<{ usuarios: ChatWhitelistUser[] }>("chat/whitelist", {
    params: q ? { q } : undefined,
  });
  return data?.usuarios || [];
};

export const toggleWhitelistChat = async (usuarioCedula: number, activo: boolean) => {
  await api.post("chat/whitelist/toggle", {
    usuario_cedula: usuarioCedula,
    activo,
  });
};

export const crearConversacionPrivada = async (usuarioDestino: number) => {
  const { data } = await api.post<{ conversacion_id: number }>("chat/conversaciones/privada", {
    usuario_destino: usuarioDestino,
  });
  return Number(data?.conversacion_id || 0);
};

export const obtenerOCrearGrupoTIC = async (integrantes: number[] = []) => {
  const { data } = await api.post<{ conversacion_id: number }>("chat/conversaciones/tic", {
    integrantes,
  });
  return Number(data?.conversacion_id || 0);
};

export const crearConversacionGrupo = async (nombre: string, integrantes: number[] = []) => {
  const { data } = await api.post<{ conversacion_id: number }>("chat/conversaciones/grupo", {
    nombre,
    integrantes,
  });
  return Number(data?.conversacion_id || 0);
};

export const listarMiembrosConversacion = async (conversacionId: number) => {
  const { data } = await api.get<{ miembros: ChatMember[] }>(`chat/conversaciones/${conversacionId}/miembros`);
  return data?.miembros || [];
};

export const agregarMiembroConversacion = async (conversacionId: number, usuarioDestino: number) => {
  await api.post(`chat/conversaciones/${conversacionId}/miembros`, {
    usuario_destino: usuarioDestino,
  });
};

export const quitarMiembroConversacion = async (conversacionId: number, usuarioDestino: number) => {
  await api.delete(`chat/conversaciones/${conversacionId}/miembros/${usuarioDestino}`);
};

export const listarMensajes = async (conversacionId: number, params?: { limit?: number; beforeId?: number }) => {
  const query: Record<string, string | number> = {};
  if (params?.limit) query.limit = params.limit;
  if (params?.beforeId) query.before_id = params.beforeId;

  const { data } = await api.get<{ mensajes: ChatMessage[] }>(`chat/conversaciones/${conversacionId}/mensajes`, {
    params: query,
  });
  return data?.mensajes || [];
};

export const enviarMensaje = async (conversacionId: number, cuerpo: string) => {
  const { data } = await api.post<{ mensaje: ChatMessage }>(`chat/conversaciones/${conversacionId}/mensajes`, {
    cuerpo,
  });
  return data?.mensaje;
};

export const marcarConversacionLeida = async (conversacionId: number, mensajeId?: number) => {
  await api.post(`chat/conversaciones/${conversacionId}/leer`, {
    mensaje_id: mensajeId,
  });
};
